export const generateMeta = (page, seo_defaults) => {
  if (!page) return []

  const config = useRuntimeConfig()
  const pageDescription = getPageDescription(page, seo_defaults)
  const pageImage = getPageImage(page, seo_defaults)

  const meta = []

  if (page.seo_hidden) {
    meta.push({ hid: 'robots', name: 'robots', content: 'noindex' })
  }

  meta.push({
    hid: 'description',
    name: 'description',
    content: pageDescription,
  })

  meta.push({
    hid: 'og:type',
    name: 'og:type',
    content: 'website',
  })

  meta.push({
    hid: 'og:title',
    property: 'og:title',
    content: getPageTitle(page),
  })

  meta.push({
    hid: 'og:description',
    property: 'og:description',
    content: pageDescription,
  })

  meta.push({
    hid: 'og:url',
    property: 'og:url',
    content: config.APP_URL + page.url,
  })

  if (pageImage) {
    meta.push({
      hid: 'og:image',
      property: 'og:image',
      content: pageImage.permalink,
    })
    meta.push({
      hid: 'og:image:alt',
      property: 'og:image:alt',
      content: pageImage.alt,
    })
    meta.push({
      hid: 'og:image:width',
      property: 'og:image:width',
      content: pageImage.width,
    })
    meta.push({
      hid: 'og:image:height',
      property: 'og:image:height',
      content: pageImage.height,
    })
    meta.push({
      hid: 'twitter:image',
      name: 'twitter:image',
      content: pageImage.permalink,
    })
    meta.push({
      hid: 'twitter:image:alt',
      name: 'twitter:image:alt',
      content: pageImage.alt,
    })
  }

  return meta
}

export const generateLinks = (page) => {
  if (!page) return []

  const config = useRuntimeConfig()
  const nuxtApp = useNuxtApp()

  const pageAlternates = Array.isArray(page.alternates)
    ? page.alternates ?? []
    : []
  const alternates = pageAlternates.map((alternatePage) => {
    // remove "/de" from url if its the root page
    const url = alternatePage.is_root
      ? alternatePage.url.substring(3)
      : alternatePage.url
    return {
      rel: 'alternate',
      hreflang: alternatePage.language + '-ch',
      href: removeTrailingSlash(config.APP_URL + url),
    }
  })

  // sort alternates by current set locale
  alternates.sort((a, b) =>
    a.hreflang.includes(nuxtApp.$i18n.locale) ? -1 : 1
  )

  // define x-default link
  const rootPage = pageAlternates.filter((p) => p.is_root)

  if (rootPage.length > 0) {
    alternates.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: removeTrailingSlash(config.APP_URL + rootPage[0]?.url.substring(3)),
    })
  }

  return [
    {
      hid: 'canonical',
      rel: 'canonical',
      href: config.APP_URL + page.url,
    },
    ...alternates,
  ]
}

// ------ HELPERS ------

// REMOVE HTML TAGS
export const removeHtmlTags = (text) => {
  if (!text) return ''

  // replace <br> and </p><p> with space and remove all other html tags
  return text.replace(/<br>|<\/p><p>/gi, ' ').replace(/<[^>]*>/g, '')
}

// TITLE
export const getPageTitle = (page) => {
  if (!page) return ''

  return removeHtmlTags(
    page.seo_title || getHeroProperty(page, 'heading') || page.title
  )
}

// DESCRIPTION
export const getPageDescription = (page, seo_defaults) => {
  if (!page) return null

  let description = removeHtmlTags(
    page.seo_description || seo_defaults.seo_description || ''
  )
  if (description.length > 155) {
    description = description.slice(0, 155) + '...'
  }
  return description
}

// HERO PROPS
export const getHeroProperty = (page, property) => {
  if (!Array.isArray(page.hero) || !page.hero.length) {
    return null
  }

  return removeHtmlTags(page.hero[0][property])
}

// IMAGE
export const getPageImage = (page, seo_defaults) => {
  if (page.seo_image) {
    return page.seo_image
  }

  if (seo_defaults.seo_image) {
    return seo_defaults.seo_image
  }

  if (!Array.isArray(page.hero) || !page.hero.length) return null
  if (!page.hero[0]?.image || !page.hero[0]?.file) return null
  const image = page.hero[0]?.image || page.hero[0]?.file
  if (!image.is_image) return null

  return image
}

// REMOVE TRAILING SLASH
export const removeTrailingSlash = (str) => {
  if (!str) return null
  return str.endsWith('/') ? str.slice(0, -1) : str
}
